import { Alert, Button, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useRequest from "@entrilia/shared/hooks/useRequest";
import adminApi from "../../../../api/adminApi";
import type { Dashboard } from "../../../../api/types/dashboardsTypes.ts";

interface Props {
  open: boolean;
  dashboard: Dashboard;
  onClose: () => void;
  onSaved: (dashboard: Dashboard) => void;
}

const DuplicateDashboardDialog = ({ open, dashboard, onClose, onSaved }: Props) => {
  const [name, setName] = useState(dashboard.nameUserInput);
  const isNameValid = name.trim().length > 0;

  const handleClose = () => {
    onClose();
  };

  const duplicateDashboard = useCallback(() => {
    return adminApi.duplicateDashboard({ id: dashboard.dashboardId, nameUserInput: name });
  }, [dashboard.dashboardId, name]);

  const { request, error, isLoading: isSaving } = useRequest(duplicateDashboard);

  const handleSave = async () => {
    const [response, error] = await request();
    if (!error) {
      onClose();
      onSaved(response.dashboard);
    }
  };

  return (
    <SmallDialog open={open} onClose={handleClose}>
      <DialogTitle>Duplicate</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error.message}</Alert>}
          <TextField
            fullWidth
            value={name}
            onChange={(evt) => setName(evt.target.value)}
            error={!isNameValid}
            helperText={!isNameValid ? "Name cannot be empty." : ""}
            label="Name"
            disabled={isSaving}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose}>
        <Button variant="contained" color="primary" loading={isSaving} disabled={!isNameValid} onClick={handleSave}>
          Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DuplicateDashboardDialog;
